import { oc } from "@orpc/contract";
import { z } from "zod";
import { listInput, listPage } from "./list";

/**
 * `invited`: has an enrolment link but no passkey or password yet. `active`: signs in with a
 * passkey or a password.
 * `deactivated`: can no longer sign in, whatever passkeys or tokens they still have.
 */
export const EmployeeStatus = z.enum(["invited", "active", "deactivated"]);
export type EmployeeStatus = z.infer<typeof EmployeeStatus>;

export const Employee = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
	status: EmployeeStatus,
	passkeyCount: z.number().int(),
	/** Whether the employee can sign in with email and password. */
	hasPassword: z.boolean(),
	/** When one of the employee's passkeys or their password last signed in, ISO 8601. */
	lastLoginAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
});
export type Employee = z.infer<typeof Employee>;

/** A passkey as the admin shows it: never its key material. */
export const Passkey = z.object({
	id: z.string(),
	createdAt: z.iso.datetime(),
	/** When it last signed in, ISO 8601. */
	lastUsedAt: z.iso.datetime().nullable(),
	/** Synced between devices (iCloud Keychain, Google Password Manager), as the authenticator said. */
	backedUp: z.boolean(),
	/** How the browser reaches it: `internal`, `hybrid` (a phone), `usb`, ... */
	transports: z.array(z.string()),
});
export type Passkey = z.infer<typeof Passkey>;

/** One employee with their passkeys: the detail page. */
export const EmployeeDetail = Employee.extend({
	deactivatedAt: z.iso.datetime().nullable(),
	passkeys: z.array(Passkey),
});
export type EmployeeDetail = z.infer<typeof EmployeeDetail>;

export const PasskeyInput = z.object({ id: z.string().max(64), passkeyId: z.string().max(64) });

export const CreateEmployeeInput = z.object({
	name: z.string().trim().min(1).max(100),
	/** Compared and stored in lower case. */
	email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
});
export type CreateEmployeeInput = z.infer<typeof CreateEmployeeInput>;

/**
 * A one-time link to attach a passkey. The token is the secret: whoever holds the link can enrol a
 * passkey for the employee until it is used or expires. Shown once; the server keeps only its hash.
 */
export const Enrolment = z.object({
	token: z.string(),
	expiresAt: z.iso.datetime(),
});
export type Enrolment = z.infer<typeof Enrolment>;

/**
 * What became of the invitation mail: `sent`, `simulated` (outside production: logged in the mail
 * log, never sent) or `failed` (the employee exists; pass the link on by hand or send it again).
 */
export const InvitationStatus = z.enum(["sent", "simulated", "failed"]);
export type InvitationStatus = z.infer<typeof InvitationStatus>;

export const CreatedEmployee = z.object({
	employee: Employee,
	enrolment: Enrolment,
	invitation: z.object({ status: InvitationStatus }),
});
export type CreatedEmployee = z.infer<typeof CreatedEmployee>;

export const ResentInvitation = z.object({
	enrolment: Enrolment,
	invitation: z.object({ status: InvitationStatus }),
});
export type ResentInvitation = z.infer<typeof ResentInvitation>;

/** An employee, by id in the path. */
export const EmployeeIdInput = z.object({ id: z.string().max(64) });

/**
 * The enrolment link for a token. The token travels in the fragment, which browsers never send to
 * a server or put in a `Referer`, so it stays out of access logs.
 */
export const enrolmentUrl = (origin: string, token: string): string => `${origin}/enrol#${token}`;

export const EmployeeListInput = listInput({
	sort: ["name", "email", "createdAt", "lastLoginAt"],
	defaultSort: "name",
	filters: { status: EmployeeStatus.optional().catch(undefined) },
});
export type EmployeeListInput = z.input<typeof EmployeeListInput>;
/** The list query with its defaults filled in, as the API receives it. */
export type EmployeeListQuery = z.output<typeof EmployeeListInput>;

export const EmployeeList = listPage(Employee);
export type EmployeeList = z.infer<typeof EmployeeList>;

/** Employees, for signed-in employees only. */
export const EmployeeContract = {
	/** A page of employees: search on name and email, filter on status. */
	list: oc.route({ method: "GET", path: "/admin/employees" }).input(EmployeeListInput).output(EmployeeList),
	/**
	 * Invites a new employee: mails them a one-time enrolment link (and returns it, to pass on by
	 * hand when the mail does not arrive); 409 when the email address is taken.
	 */
	create: oc
		.route({ method: "POST", path: "/admin/employees" })
		.input(CreateEmployeeInput)
		.output(CreatedEmployee),
	/** Mails a fresh enrolment link (earlier unused links stop working); 404 for an unknown id. */
	resendInvitation: oc
		.route({ method: "POST", path: "/admin/employees/{id}/invitation" })
		.input(EmployeeIdInput)
		.output(ResentInvitation),
	/** One employee with their passkeys; 404 for an unknown id. */
	get: oc
		.route({ method: "GET", path: "/admin/employees/{id}" })
		.input(EmployeeIdInput)
		.output(EmployeeDetail),
	/**
	 * Removes one of the employee's passkeys, and the sessions it opened; 409 `LastPasskey` for the
	 * only one they have.
	 */
	removePasskey: oc
		.route({ method: "DELETE", path: "/admin/employees/{id}/passkeys/{passkeyId}" })
		.input(PasskeyInput),
	/**
	 * Deactivates the employee: their sessions end at once and nothing of theirs signs in again;
	 * 409 `CannotDeactivateYourself` for the signed-in employee.
	 */
	deactivate: oc
		.route({ method: "POST", path: "/admin/employees/{id}/deactivate" })
		.input(EmployeeIdInput)
		.output(EmployeeDetail),
	/**
	 * Reactivates a deactivated employee: they sign in again with their remaining passkeys (with
	 * none, mail them a fresh link with `resendInvitation`). Sessions and tokens from before stay
	 * ended and revoked. 404 for an unknown id; an active employee is left as is.
	 */
	reactivate: oc
		.route({ method: "POST", path: "/admin/employees/{id}/reactivate" })
		.input(EmployeeIdInput)
		.output(EmployeeDetail),
};
